import { useState, useCallback, useRef } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'

import type { IntegrationsSyncRunFragment } from 'generated/resources/graphql'
import { SyncRunOutputsDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { download } from 'modules/core/lib/download'
import { getSyncRunStatusIdSessionStorage } from 'modules/integrations/lib/syncRun'
import { isSyncRunRunning } from 'modules/integrations/lib/syncRun'

interface UseSyncRunFileDownloadProps {
  isBookkeeperView: boolean
  lastSyncRun: IntegrationsSyncRunFragment | undefined | null
}

export const useSyncRunFileDownload = ({
  isBookkeeperView,
  lastSyncRun,
}: UseSyncRunFileDownloadProps) => {
  const [fetchSyncRunOutputs] = useLazyQuery(SyncRunOutputsDocument)
  const [syncRunOutputsLoading, setSyncRunOutputsLoading] = useState(false)

  // Detect running → finished transition and trigger file download
  const wasRunningRef = useRef(false)
  const isRunning = isSyncRunRunning(lastSyncRun ?? undefined)
  const lastSyncRunId = lastSyncRun?.id ?? null
  const lastSyncRunFinishedAt = lastSyncRun?.finishedAt ?? null

  const downloadSyncRunOutput = useCallback(
    (syncRunId: string, finishedAt: string | null) => {
      const fileDownloadAvailable = getSyncRunStatusIdSessionStorage(syncRunId)
      if (!fileDownloadAvailable || !finishedAt) return

      setSyncRunOutputsLoading(true)
      fetchSyncRunOutputs({
        variables: { syncRunIds: [syncRunId] },
        context: { isBookkeeperView },
      })
        .then((result) => {
          const file = result.data?.integrations?.syncRunOutputsConnection?.nodes?.[0]
          if (file?.fileUrl) {
            download(file.fileUrl, file.fileName)
          }
        })
        .finally(() => setSyncRunOutputsLoading(false))
    },
    [fetchSyncRunOutputs, isBookkeeperView]
  )

  useEffect(() => {
    if (wasRunningRef.current && !isRunning && lastSyncRunId) {
      downloadSyncRunOutput(lastSyncRunId, lastSyncRunFinishedAt)
    }
    wasRunningRef.current = isRunning
  }, [isRunning, lastSyncRunId, lastSyncRunFinishedAt, downloadSyncRunOutput])

  return { syncRunOutputsLoading }
}
